import React, { useState, useRef, useEffect } from "react";
import TextInput from "../components/ui/TextInput";
import UploadFileCard from "../components/ui/UploadFileCard";
import toast from "../lib/utils/toast";
import {
  uploadFiles,
  getContentHistory,
  generateVideoV2,
  checkVideoGenerationStatus,
  generateSoraAndGenTurboVideo,
  checkSoraVideoGenerationStatus,
  checkGenTuroVideoGenerationStatus,
} from "../lib/redux/actions";
import InfiniteScroll from "react-infinite-scroll-component";
import classNames from "classnames";
import { Download, ChevronDown, X } from "lucide-react";
import { downloadFile, getModelData } from "../lib/utils/utils";
import daburLogo from "../assets/svg/appIcon.svg";
import Avatar from "../assets/svg/avatar.svg";

import RotatingTextLoader from "../lib/common/components/loader/RotatingTextLoader";
import { useOutletContext } from "react-router-dom";
import UploadStepper from "../components/generateVideo/UploadStepper";
import {
  trackEvent,
  trackException,
  trackTrace,
} from "../lib/utils/telemetryService";
import { MESSAGES } from "../config/messages";
import PPTSkeletonLoader from "../components/presentation/PPTSkeletonLoader";

export default function GenerateVideo() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [chats, setChats] = useState([]);
  const scrollRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(true);
  const [modelName, setModelName] = useState("gen4.5");
  const [showStrictWarning, setShowStrictWarning] = useState(true);

  const { isMobile, setModelSelectorConfig, videoModels } = useOutletContext();

  // Build model options map from API response, with fallback to static options
  const MODEL_OPTIONS =
    videoModels && videoModels.length > 0
      ? videoModels.reduce((acc, model) => {
        if (model?.modelKey && model?.modelName) {
          acc[model.modelKey] = model.modelName;
        }
        return acc;
      }, {})
      : {};

  // Derive requirements from backend-provided model config
  const currentVideoModel =
    (videoModels || []).find((m) => m?.modelKey === modelName) || null;

  // If imageRequired is true => prompt + image, if false => prompt only
  const requiresImage = currentVideoModel?.imageRequired === true;
  const requiresOnlyPrompt = currentVideoModel?.imageRequired === false;

  useEffect(() => {
    handleGetContentHistory(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Ensure selected model exists in the available video models
  useEffect(() => {
    if (videoModels && videoModels.length > 0) {
      const hasCurrent = videoModels.some((m) => m?.modelKey === modelName);
      if (!hasCurrent) {
        setModelName(videoModels[0].modelKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoModels]);

  // Register model selector in header
  useEffect(() => {
    // Only set config if we have models available
    if (
      setModelSelectorConfig &&
      videoModels &&
      videoModels.length > 0 &&
      Object.keys(MODEL_OPTIONS).length > 0
    ) {
      setModelSelectorConfig({
        modelName,
        modelOptions: MODEL_OPTIONS,
        modelList: videoModels || [],
        onModelChange: (newModel) => {
          setModelName(newModel);

          // Show warning banner when switching to strict models
          const isStrictModel = newModel === "gen4.5" || newModel === "sora";
          if (isStrictModel) {
            setShowStrictWarning(true);
          }

          // Clear files if switching to a model that doesn't require image
          const selectedModel =
            (videoModels || []).find((m) => m?.modelKey === newModel) || null;
          const selectedRequiresOnlyPrompt =
            selectedModel?.imageRequired === false;

          if (selectedRequiresOnlyPrompt && files.length > 0) {
            setFiles([]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            toast.info("Image removed. This model requires only prompt input.");
          }
        },
      });
    }
    return () => {
      if (setModelSelectorConfig) {
        setModelSelectorConfig(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelName, setModelSelectorConfig, files.length, videoModels]);

  // Clear files when switching to a model that doesn't require image
  useEffect(() => {
    if (requiresOnlyPrompt && files.length > 0) {
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelName]);

  const handleGetContentHistory = async (pageNumber = 1, isInitialLoad = false) => {
    if (isFetchingRef.current) return;

    trackEvent("FetchChatsBySessionID", { pageNumber });
    try {
      if (isInitialLoad) setIsChatLoading(true);
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);
      trackEvent("fetchChatHistory_GenerateVideo_Start", { pageNumber });
      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 4,
        chatType: 3, // VIDEO type
      };
      const res = await getContentHistory(data);
      const newChats = res.data.data || [];

      // Filter out duplicate entries (where questionId is null)
      const filteredChats = newChats.filter((chat) => chat.questionId !== null);

      const existingIds = new Set(chats.map((chat) => chat._id));
      const uniqueNewChats = filteredChats.filter(
        (chat) => !existingIds.has(chat._id)
      );

const processedMessages = uniqueNewChats.flatMap((chat) => {


  return [
    {
      role: "user",
      message: chat.questionId.message,
      modelName: chat?.modelName,
      modelId: getModelData(videoModels, chat?.modelName)?.modelId,
      _id: chat.questionId._id,
      type: "text",
    },
    {
      role: "assistant",
      message: chat.content,
      _id: chat._id,
      type: "video",
    },
  ];
});



      setChats((prev) => [...processedMessages, ...prev]);
      setHasMore(processedMessages.length > 0);

      // After state update, adjust scroll position
      trackEvent("fetchChatHistory_GenerateVideo", {
        pageNumber,
        fetched: processedMessages?.length,
      }); // [TRACKING LINE: 74]
      trackTrace(`Fetched ${processedMessages?.length} video chat(s)`);

      // setTimeout(() => {
      //   if (scrollRef.current) {
      //     const newScrollHeight = scrollRef.current.scrollHeight;
      //     const scrollDiff = newScrollHeight - scrollHeightRef.current;
      //     scrollRef.current.scrollTop = scrollDiff;
      //   }
      // }, 0);
    } catch (error) {
      trackException(error);
      trackEvent("fetchChatHistory_GenerateVideo_Failed");
      console.error(error);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (isInitialLoad) setIsChatLoading(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
    }
  };

  const handleGenerate = async ({ text, media = [] } = {}) => {
    const userPrompt = text || prompt;
    const data = getModelData(videoModels, modelName);


    // Validation based on model requirements
    if (requiresImage) {
      // Models requiring both prompt and image
      if (!userPrompt?.trim()) {
        toast.error("Please enter a prompt", {
          duration: 5000,
        });
        return;
      }
      if (files.length === 0) {
        toast.error("Please upload an image file before generating the video", {
          duration: 5000,
        });
        return;
      }
    } else if (requiresOnlyPrompt) {
      // Models requiring only prompt (no image)
      if (!userPrompt?.trim()) {
        toast.error("Please enter a prompt", {
          duration: 5000,
        });
        return;
      }
      // Don't allow image for these models
      if (files.length > 0) {
        toast.error(
          "This model does not support image upload. Please remove the image and try again.",
          {
            duration: 5000,
          }
        );
        return;
      }
    }
    // Clear input
    setPrompt("");
    trackEvent("GenerateVideo_Attempt", {
      promptLength: (userPrompt || "").length,
      model: modelName,
    }); // [TRACKING LINE: 108]
    trackTrace(
      `GenerateVideo payload prompt: ${userPrompt?.slice(
        0,
        200
      )}, model: ${modelName}`
    );
    setChats((prev) => [
      ...prev,
      { role: "user", message: userPrompt, type: "text",modelId:data?.modelId, },
      { role: "assistant", message: "Generating video...", type: "loading" },
    ]);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);
    setLoading(true);
    const payload = {
      prompt: userPrompt,
      model_name: modelName,
      ...(requiresImage && files[0]?.fileUrl && { url: files[0].fileUrl }),
    };
    try {
      // Clear any existing polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (modelName === "gen4.5" || modelName === "sora") {
        // Use Unified API for gen4_turbo and sora
        const res = await generateSoraAndGenTurboVideo(payload);
        const { data } = res;
        // Check if we have a generation_id to poll
        if (data?.task_id) {
          const loadingMessageId = `loading-${Date.now()}`;

          // Update loading message with the generation_id for reference
          setChats((prev) => {
            const updated = [...prev];
            const lastIndex = updated
              .map((c) => c.role + c.type)
              .lastIndexOf("assistantloading");
            if (lastIndex !== -1) {
              updated[lastIndex] = {
                role: "assistant",
                message: "Generating video...",
                type: "loading",
                _id: loadingMessageId,
                generationId: data.task_id,
              };
            }
            return updated;
          });

          // Start polling for status
          pollingIntervalRef.current = setInterval(async () => {
            try {
              let statusRes;
              if (modelName === "sora") {
                statusRes = await checkSoraVideoGenerationStatus(
                  data.task_id
                );
              } else {
                statusRes = await checkGenTuroVideoGenerationStatus(
                  data.task_id
                );
              }
              const statusData = statusRes.data;

              // Status: "1" or "2" = pending, "3" = success, "4" = failed

              if (statusData?.status === "completed") {
                // Success - update with video URL
                const finalMessage = {
                  role: "assistant",
                  message: statusData?.outputUrl || statusData?.content || "",
                  type: "video",
                  _id: statusData?._id || loadingMessageId,
                };

                setChats((prev) => {
                  const updated = prev.map((chat) =>
                    chat._id === loadingMessageId ? finalMessage : chat
                  );
                  return updated;
                });
                setLoading(false);
                trackEvent("GenerateVideo_Success", {
                  generationId: data.task_id,
                  videoId: statusData?._id,
                });
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              } else if (statusData?.status?.toLowerCase() === "failed") {
                // Failed
                const errorMessage = {
                  role: "assistant",
                  message:
                    statusData?.error || "I can not generate a Video from the prompt you have given",
                  type: "error",
                  _id: loadingMessageId,
                };

                setChats((prev) => {
                  const updated = prev.map((chat) =>
                    chat._id === loadingMessageId ? errorMessage : chat
                  );
                  return updated;
                });
                setLoading(false);
                trackException(new Error("Video generation failed"));
                trackEvent("GenerateVideo_Failed", {
                  generationId: data.task_id,
                });
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              // If status is "1" or "2", continue polling (do nothing)
            } catch (error) {
              console.error("Error fetching video generation status:", error);
              trackException(error);
              // On error, stop polling and show error
              const errorMessage = {
                role: "assistant",
                message:
                  "I can not generate a Video from the prompt you have given",
                type: "error",
                _id: loadingMessageId,
              };

              setChats((prev) => {
                const updated = prev.map((chat) =>
                  chat._id === loadingMessageId ? errorMessage : chat
                );
                return updated;
              });
              setLoading(false);
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }, 15000); // Poll every 15 seconds
        } else {
          throw new Error("No generation_id received from API");
        }
      } else {
        // Use V2 API with polling for other models
        const res = await generateVideoV2(payload);
        const { data } = res;

        // Check if we have a generation_id to poll
        if (data?.generation_id) {
          const loadingMessageId = `loading-${Date.now()}`;

          // Update loading message with the generation_id for reference
          setChats((prev) => {
            const updated = [...prev];
            const lastIndex = updated
              .map((c) => c.role + c.type)
              .lastIndexOf("assistantloading");
            if (lastIndex !== -1) {
              updated[lastIndex] = {
                role: "assistant",
                message: "Generating video...",
                type: "loading",
                _id: loadingMessageId,
                generationId: data.generation_id,
              };
            }
            return updated;
          });

          // Start polling for status
          pollingIntervalRef.current = setInterval(async () => {
            try {
              const statusRes = await checkVideoGenerationStatus(data.generation_id);
              const statusData = statusRes.data;

              // Status: "1" or "2" = pending, "3" = success, "4" = failed
              if (statusData?.status === "3") {
                // Success - update with video URL
                const finalMessage = {
                  role: "assistant",
                  message: statusData?.outputUrl || statusData?.content || "",
                  type: "video",
                  _id: statusData?._id || loadingMessageId,
                };

                setChats((prev) => {
                  const updated = prev.map((chat) =>
                    chat._id === loadingMessageId ? finalMessage : chat
                  );
                  return updated;
                });
                setLoading(false);
                trackEvent("GenerateVideo_Success", {
                  generationId: data.generation_id,
                  videoId: statusData?._id
                });
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              } else if (statusData?.status === "4") {
                // Failed
                const errorMessage = {
                  role: "assistant",
                  message: "I can not generate a Video from the prompt you have given",
                  type: "error",
                  _id: loadingMessageId,
                };

                setChats((prev) => {
                  const updated = prev.map((chat) =>
                    chat._id === loadingMessageId ? errorMessage : chat
                  );
                  return updated;
                });
                setLoading(false);
                trackException(new Error("Video generation failed"));
                trackEvent("GenerateVideo_Failed", {
                  generationId: data.generation_id
                });
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              // If status is "1" or "2", continue polling (do nothing)
            } catch (error) {
              console.error("Error fetching video generation status:", error);
              trackException(error);
              // On error, stop polling and show error
              const errorMessage = {
                role: "assistant",
                message: "I can not generate a Video from the prompt you have given",
                type: "error",
                _id: loadingMessageId,
              };

              setChats((prev) => {
                const updated = prev.map((chat) =>
                  chat._id === loadingMessageId ? errorMessage : chat
                );
                return updated;
              });
              setLoading(false);
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }, 15000); // Poll every 15 seconds (same as DeepSearchChatInterface)
        } else {
          // No generation_id, treat as error
          throw new Error("No generation_id received from API");
        }
      }
    } catch (error) {
      setChats((prev) => {
        const updated = [...prev];
        const lastIndex = updated
          .map((c) => c.role + c.type)
          .lastIndexOf("assistantloading");
        if (lastIndex !== -1) {
          updated[lastIndex] = {
            role: "assistant",
            message:
              "I can not generate a Video from the prompt you have given",
            type: "error",
          };
        }
        return updated;
      });
      setLoading(false);
      trackException(error);
      trackEvent("GenerateVideo_Failed");

      // Clear polling if it exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  const fetchMoreData = async () => {
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsLoadingMore(true);

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      setPage(nextPage);
      await handleGetContentHistory(nextPage);
    }, 300); // 300ms debounce
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Prevent file upload if model doesn't require image
    if (requiresOnlyPrompt) {
      toast.error("This model does not support image upload. Please select a different model.", {
        duration: 5000,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    const MAX_FILE_SIZE_MB = 50; // Increased size limit for videos
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        toast.error(`Unsupported format: ${file.name}`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`File size is too big (Maximum allowed size is 50MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      trackEvent("GenerateVideo_FileUpload_Start", { fileCount: validFiles.length });
      const result = await uploadFiles(formData);

      if (result.statusCode === 200) {
        trackEvent("GenerateVideo_FileUpload_Success", { uploaded: result.data.length });
        setFiles(
          result.data.map((url, index) => ({
            fileUrl: url,
            fileName: validFiles[index].name,
          }))
        );
      }
    } catch (error) {
      trackException(error);
      trackEvent("GenerateVideo_FileUpload_Failed");
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const showSampleGrid = !isLoadingHistory && chats.length === 0;

  // Add scroll to bottom function
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  if (isChatLoading) {
    return <PPTSkeletonLoader />;
  }

  return (
    <div
      className={`flex-1 flex flex-col justify-between mt-8 overflow-scroll sm:overflow-hidden mx-4`}
    >
      {/* Strict Model Warning Banner */}
      {showStrictWarning && (modelName === "gen4.5" || modelName === "sora") && (
        <div className={`fixed top-[60px]  right-0 min-h-10 bg-[#f8ff00] border-b border-[#FFCA2C] z-20 flex items-center justify-center z-[10] py-1 px-2 sm:px-4 overflow-visible w-full`}>
          <h1 className={`${isMobile ? "ml-[0%]" : "ml-72"} text-center text-black text-sm sm:text-base font-medium`}>
            Strict Limit Filtration: Avoid using brand name in prompt and images
          </h1>
          <button
            onClick={() => setShowStrictWarning(false)}
            className="ml-auto flex-shrink-0 text-black  transition-colors"
            aria-label="Close warning"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {showSampleGrid && (
        <>
          <h1
            className={`xl:text-4xl lg:text-xl lg:mt-20 md:mt-10 max-w-[768px] m-auto ${isMobile ? "text-left " : "text-center"
              } mb-8 text-gray-800`}
          >
            Welcome to DaburGPT Video Agent. To generate a video, upload the
            image.
          </h1>
          <div className="flex justify-center">
            <UploadStepper />
          </div>
        </>
      )}

      {!showSampleGrid && (
        <div
          id="scrollableDiv"
          ref={scrollRef}
          className={`flex-1 py sm:px-[50px] md:px-[20px] lg:px-[100px]  overflow-y-auto  xl:!px-[220px] 2xl:!px-[220px] flex flex-col-reverse mt-[40px] ${!isMobile && " !px-[30px]"
            }`}
        >
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
          <InfiniteScroll
            inverse={true}
            dataLength={chats.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={null}
            endMessage={
              chats?.totalPages >= 2 ? (
                <p className="text-center m-2 font-segoeUI">No More chats!</p>
              ) : (
                ""
              )
            }
            scrollThreshold="200px"
            className="!overflow-hidden"
          // style={{ overflow: "hidden" }}
          >
            {chats.map((chat, idx) => (
              <div
                key={chat?._id}
                className={`flex mb-4 gap-4  sm:w-full ${chat.role === "user" ? "justify-end" : "justify-start"
                  }`}
              >
                {chat.role !== "user" && !isMobile && (
                  <div className="w-[40px] align-self-center flex-shrink-0">
                    <img
                      src={daburLogo}
                      alt="Dabur Logo"
                      className="w-[40px]"
                    />
                  </div>
                )}
                <div className="flex flex-col items-end">
                <div
                  className={`text-[#000000] flex flex-col gap-2 overflow-x-auto   min-h-[44px] justify-center w-fit group  ${chat.role === "user"
                      ? " bg-[#1b5c2926] rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] max-w-[85%] sm:max-w-[75%] md:max-w-[70%] w-fit"
                      : ""
                    }`}
                >
                  {chat.type === "text" && (
                    <>
                      <span className="p-2">{chat.message}</span>

                    </>
                  )}
                  {chat.type === "loading" && (
                    <span className="p-2">
                      <RotatingTextLoader messages={[
                        "Analyzing video request",
                        "Setting up scene composition",
                        "Generating video frames",
                        "Stitching sequences together",
                        "Finalizing video rendering"
                      ]} />
                    </span>
                  )}
                  {chat.type === "error" && (
                    <span className="text-red-500 p-2">{chat.message}</span>
                  )}
                  {chat.type === "video" && (
                    <div
                      className="!relative !inline-block"
                    // style={{ position: "relative", display: "inline-block" }}
                    >
                      <video
                        controls
                        className="rounded-xl w-[400px] h-[300px] object-cover"
                      >
                        <source src={chat.message} type="video/mp4" />
                        <track kind="captions" label="No captions available" src="" />

                        Your browser does not support the video element.
                      </video>
                      <button
                        className="absolute top-3 right-3 bg-white bg-opacity-80 border-none rounded-full p-2 cursor-pointer shadow-md"
                        onClick={() => {
                          downloadFile(chat.message, "generated-video.mp4");
                        }}
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                </div>
                 {chat.role === "user" &&<div className="flex items-center justify-end mt-1">
                      {chat?.modelId && (
                        <div
                          className="text-[12px] text-gray-500 flex items-center gap-1 ml-3"
                          style={{marginTop: '0px'}}>
                          <span className="ml-2 text-gray-500">
                            <i>Model:{chat.modelId}</i>
                          </span>
                            
                        </div>
                      )}
                    </div>
               }
               </div>

                {chat.role === "user" && !isMobile && (
                  <div className="!w-[35px] flex-shrink-0">
                    <img src={Avatar} alt="Dabur Logo" className="w-[40px]" />
                  </div>
                )}
              </div>
            ))}
          </InfiniteScroll>

          {isLoadingMore && !isLoadingHistory && (
            <div className="flex justify-center items-center gap-2 my-4 py-2 w-full">
              <svg
                className="animate-spin"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1B5C29"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className="text-sm text-[#1B5C29] font-medium">Loading more...</span>
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          `sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-4  sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px] ${!isMobile && " !px-[30px]"
          }`
        )}
      >
        <div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((file, index) => (
                <UploadFileCard
                  key={`uploadfilecard-${file?.fileUrl}}`}
                  file={file}
                  index={index}
                  removeFile={removeFile}
                />
              ))}
            </div>
          )}
        </div>
        <div className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
          <TextInput
            inputRef={inputRef}
            placeholder={requiresOnlyPrompt ? "Enter a description to generate a video" : "Upload an image and enter a description to generate a video"}
            inputText={prompt}
            handleChatResponse={handleGenerate}
            setInputText={setPrompt}
            isVideo={true}
            inputFocused={inputFocused}
            setInputFocused={setInputFocused}
            loading={loading}
            disabled={loading && chats.length > 0}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            isUploading={isUploading}
            files={files}
            setFiles={setFiles}
            chats={chats}
            allowedFileTypes="image/jpeg,image/png,image/jpg,image/gif,image/webp"
            isContentRendering={loading}
            isFileUploadOptionVisible={!requiresOnlyPrompt}
          // requireMediaToSend={requiresImage}
          />
        </div>
        <div className="max-w-4xl mx-auto mt-[40px]">
          <p className="text-xs text-gray-500 text-center">
            {MESSAGES.POWERED_BY}
          </p>
        </div>
      </div>
    </div>
  );
}
